import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion2';
import { Button } from '@/components/ui/button';
import { InputLabel } from '@/components/ui/InputLabel';
import { InputNumberWithSuffix } from '@/components/ui/InputNumberWithSuffix';
import {
  Select,
  SelectItem,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import Title from '@/components/ui/Title';
import {
  addEvergreenChatMessage,
  updateEvergreenChatMessage,
  importEvergreenChat,
  fetchEvergreenChatConfiguration,
  fetchSessionChatExport,
} from '@/features/webinar/webinarThunks';
import type {
  EvergreenChatAuthorRole,
  EvergreenManualAttendeeLabel,
  EvergreenTimelineEvent,
} from '@/features/webinar/types';
import {
  CHAT_AUTHOR_NAME_MAX_LENGTH,
  CHAT_CONTENT_MAX_LENGTH,
  MANUAL_ATTENDEE_LABELS,
  buildManualChatMessageInput,
  formatAttendeeLabelOption,
  isManualAttendeeLabel,
  manualMessageToFormValues,
  resolveChatErrorTranslationKey,
} from '@/lib/evergreenManualChatMessage';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Loader from '@/components/ui/Loader';
import { cn } from '@/lib/utils';

const ROLE_OPTIONS: { value: EvergreenChatAuthorRole }[] = [
  { value: 'PRESENTER' },
  { value: 'MODERATOR' },
  { value: 'ATTENDEE' },
];

const LABEL_TRANSLATION_KEYS: Record<EvergreenManualAttendeeLabel, string> = {
  TOP_25_PERCENT: 'viewerStatus.criteria.top25Percent',
  TOP_50_PERCENT: 'viewerStatus.criteria.top50Percent',
  TOP_75_PERCENT: 'viewerStatus.criteria.top75Percent',
  TOP_100_PERCENT: 'viewerStatus.criteria.top100Percent',
  CONVERSION: 'viewerStatus.criteria.conversion',
};

const NO_LABEL_VALUE = 'NONE';

const MESSAGES_ACCORDION_VALUE = 'item-1';

const LENGTH_COUNTER_THRESHOLD = 0.8;

function LengthCounter({ length, max }: { length: number; max: number }) {
  if (length < max * LENGTH_COUNTER_THRESHOLD) return null;
  return (
    <span
      className={cn(
        'absolute top-0.5 right-3 text-xs pointer-events-none',
        length >= max ? 'text-error' : 'text-tertiary',
      )}
    >
      {length}/{max}
    </span>
  );
}

type MessageCreateProps = {
  openTrigger?: number;
  isEdit?: boolean;
  readOnly?: boolean;
  editingMessage?: EvergreenTimelineEvent | null;
  onEditEnd?: () => void;
};

export default function MessageCreate({
  openTrigger = 0,
  isEdit = false,
  readOnly,
  editingMessage = null,
  onEditEnd,
}: MessageCreateProps) {
  const locked = readOnly === true;
  const { t } = useTranslation('createWebinar');
  const dispatch = useAppDispatch();
  const workspaceId = useAppSelector((state: RootState) => state.workspace.currentWorkspace?.id);
  const { webinarId: webinarIdParam } = useParams<{ webinarId?: string }>();
  const webinarId = typeof webinarIdParam === 'string' ? parseInt(webinarIdParam, 10) : undefined;
  const {
    evergreenChatConfigurationLoading,
    evergreenChatImportLoading,
    currentSessionConfiguration,
    participationStatuses,
  } = useAppSelector((state: RootState) => state.webinar);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLDivElement>(null);

  // Le parent remonte ce composant (key) à chaque entrée/sortie d'édition : le formulaire part
  // de ces valeurs initiales, sans resynchronisation par effet.
  const [initialValues] = useState(() =>
    editingMessage ? manualMessageToFormValues(editingMessage) : null,
  );
  const [accordionValue, setAccordionValue] = useState(
    editingMessage || openTrigger > 0 ? MESSAGES_ACCORDION_VALUE : '',
  );
  const [handledOpenTrigger, setHandledOpenTrigger] = useState(openTrigger);
  if (openTrigger !== handledOpenTrigger) {
    setHandledOpenTrigger(openTrigger);
    setAccordionValue(MESSAGES_ACCORDION_VALUE);
  }
  const [hours, setHours] = useState(String(initialValues?.hours ?? 0));
  const [minutes, setMinutes] = useState(String(initialValues?.minutes ?? 0));
  const [seconds, setSeconds] = useState(String(initialValues?.seconds ?? 0));
  const [authorName, setAuthorName] = useState(initialValues?.authorName ?? '');
  const [authorRole, setAuthorRole] = useState<EvergreenChatAuthorRole>(
    initialValues?.authorRole ?? 'ATTENDEE',
  );
  const [statusCriteria, setStatusCriteria] = useState<EvergreenManualAttendeeLabel | null>(
    initialValues?.statusCriteria ?? null,
  );
  const [content, setContent] = useState(initialValues?.content ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const editingMessageId = editingMessage?.id;
  useEffect(() => {
    if (editingMessageId != null) {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [editingMessageId]);

  const labelOptions = useMemo(
    () =>
      MANUAL_ATTENDEE_LABELS.map((criteria) => ({
        value: criteria,
        label: formatAttendeeLabelOption(
          t(LABEL_TRANSLATION_KEYS[criteria]),
          participationStatuses.find((status) => status.criteria === criteria)?.label,
        ),
      })),
    [t, participationStatuses],
  );

  const parseNum = (v: string, max: number) => {
    const n = parseInt(v || '0', 10);
    if (isNaN(n) || n < 0) return 0;
    return Math.min(n, max);
  };

  const handleRoleChange = (role: EvergreenChatAuthorRole) => {
    setAuthorRole(role);
    if (role !== 'ATTENDEE') {
      setStatusCriteria(null);
    }
  };

  const handleLabelChange = (value: string) => {
    setStatusCriteria(isManualAttendeeLabel(value) ? value : null);
  };

  const showRequestError = (payload: unknown) => {
    const error = payload as { message?: string; apiCode?: string; details?: unknown } | undefined;
    const chatErrorKey = resolveChatErrorTranslationKey(error);
    const msg = chatErrorKey
      ? t(chatErrorKey)
      : error?.message
        ? t(error.message, 'Une erreur est survenue.')
        : t('errors.generic', 'Une erreur est survenue.');
    toast.error(msg);
  };

  const handleSubmit = async () => {
    if (locked) return;
    if (!workspaceId || !webinarId || isNaN(webinarId)) return;
    if (!authorName.trim()) {
      toast.error(t('chatEvergreenForm.errors.firstNameRequired'));
      return;
    }
    if (!content.trim()) {
      toast.error(t('chatEvergreenForm.errors.messageRequired'));
      return;
    }
    const input = buildManualChatMessageInput({
      hours: parseNum(hours, 999),
      minutes: parseNum(minutes, 59),
      seconds: parseNum(seconds, 59),
      authorName,
      authorRole,
      content,
      statusCriteria,
    });
    setIsSubmitting(true);

    if (editingMessage) {
      const result = await dispatch(
        updateEvergreenChatMessage({
          workspaceId,
          webinarId,
          messageId: editingMessage.id,
          input,
        }),
      );
      setIsSubmitting(false);
      if (updateEvergreenChatMessage.fulfilled.match(result)) {
        toast.success(t('chatEvergreenForm.success.messageUpdated'));
        onEditEnd?.();
      } else if (updateEvergreenChatMessage.rejected.match(result)) {
        showRequestError(result.payload);
      }
      return;
    }

    const result = await dispatch(addEvergreenChatMessage({ workspaceId, webinarId, input }));
    setIsSubmitting(false);
    if (addEvergreenChatMessage.fulfilled.match(result)) {
      toast.success(t('chatEvergreenForm.success.messageAdded'));
      setHours('0');
      setMinutes('0');
      setSeconds('0');
      setAuthorName('');
      setAuthorRole('ATTENDEE');
      setStatusCriteria(null);
      setContent('');
    } else if (addEvergreenChatMessage.rejected.match(result)) {
      showRequestError(result.payload);
    }
  };

  const handleExportCsvClick = async () => {
    if (locked) return;
    const evergreenSession = currentSessionConfiguration?.evergreenSession;
    if (!workspaceId || !evergreenSession) {
      toast.error(t('chatEvergreenForm.errors.noSessionSelected'));
      return;
    }
    const { webinarId: exportWebinarId, sessionId } = evergreenSession;
    setIsExporting(true);
    const result = await dispatch(
      fetchSessionChatExport({ workspaceId, webinarId: exportWebinarId, sessionId }),
    );
    setIsExporting(false);
    if (fetchSessionChatExport.fulfilled.match(result) && result.payload) {
      const blob = result.payload as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chat-session-${sessionId}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(t('chatEvergreenForm.success.exportDownloaded'));
    } else if (fetchSessionChatExport.rejected.match(result)) {
      showRequestError(result.payload);
    }
  };

  const handleImportCsvClick = () => {
    if (locked) return;
    fileInputRef.current?.click();
  };

  const handleImportCsvChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (locked) return;
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !workspaceId || !webinarId || isNaN(webinarId)) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error(t('chatEvergreenForm.errors.importFileRequired'));
      return;
    }
    const result = await dispatch(importEvergreenChat({ workspaceId, webinarId, file }));
    if (importEvergreenChat.fulfilled.match(result)) {
      toast.success(t('chatEvergreenForm.success.importSuccess'));
      await dispatch(fetchEvergreenChatConfiguration({ workspaceId, webinarId }));
    } else if (importEvergreenChat.rejected.match(result)) {
      showRequestError(result.payload);
    }
  };

  const disabled = evergreenChatConfigurationLoading || isSubmitting || locked;
  const canExport = !!currentSessionConfiguration?.evergreenSession && !!workspaceId;
  const isEditingMessage = editingMessage != null;

  return (
    <Accordion type="single" collapsible value={accordionValue} onValueChange={setAccordionValue}>
      <AccordionItem
        value={MESSAGES_ACCORDION_VALUE}
        className={`bg-white p-0 ${isEdit ? 'accordion-page' : ''}`}
      >
        <AccordionTrigger className="">{t('chatEvergreenForm.accordionTitle')}</AccordionTrigger>
        <AccordionContent
          className={`flex flex-col gap-4 bg-medium rounded-[20px] p-6 ${isEdit ? 'mb-4' : ''}`}
        >
          <div
            ref={formRef}
            className={cn(
              'flex flex-col gap-4 scroll-mt-24',
              locked && 'pointer-events-none select-none opacity-90',
            )}
          >
            <Title level={2}>
              {isEditingMessage
                ? t('chatEvergreenForm.editMessage')
                : t('chatEvergreenForm.addMessage')}
            </Title>
            <div className="flex flex-col gap-2">
              <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between w-full gap-2">
                <div className="flex items-center gap-2 w-full lg:flex-1 lg:min-w-0">
                  <InputNumberWithSuffix
                    label={t('time.hourLabel')}
                    suffix="hr"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    className="w-1/3 lg:w-1/3"
                    inputClassName="w-full pr-14"
                    min={0}
                    max={999}
                    maxLength={3}
                    disabled={disabled}
                  />
                  <InputNumberWithSuffix
                    label={t('time.minuteLabel')}
                    suffix="min"
                    value={minutes}
                    onChange={(e) => setMinutes(e.target.value)}
                    className="w-1/3 lg:w-1/3"
                    inputClassName="w-full pr-14"
                    min={0}
                    max={59}
                    maxLength={2}
                    disabled={disabled}
                  />
                  <InputNumberWithSuffix
                    label={t('time.secondLabel')}
                    suffix="sec"
                    value={seconds}
                    onChange={(e) => setSeconds(e.target.value)}
                    className="w-1/3 lg:w-1/3"
                    inputClassName="w-full pr-14"
                    min={0}
                    max={59}
                    maxLength={2}
                    disabled={disabled}
                  />
                </div>
                <div className="relative w-full lg:flex-1 lg:min-w-0">
                  <InputLabel
                    label={t('chatEvergreenForm.firstNameLabel')}
                    className="w-full"
                    value={authorName}
                    onChange={(e) => setAuthorName(e.target.value)}
                    maxLength={CHAT_AUTHOR_NAME_MAX_LENGTH}
                    disabled={disabled}
                  />
                  <LengthCounter length={authorName.length} max={CHAT_AUTHOR_NAME_MAX_LENGTH} />
                </div>
                <Select
                  value={authorRole}
                  onValueChange={(v) => handleRoleChange(v as EvergreenChatAuthorRole)}
                  disabled={disabled}
                >
                  <SelectTrigger
                    label={t('chatEvergreenForm.roleLabel')}
                    className="h-14 w-full lg:w-auto lg:min-w-[140px]"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {t(`chatEvergreenForm.roles.${opt.value.toLowerCase()}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {authorRole === 'ATTENDEE' && (
                  <Select
                    value={statusCriteria ?? NO_LABEL_VALUE}
                    onValueChange={handleLabelChange}
                    disabled={disabled}
                  >
                    <SelectTrigger
                      label={t('chatEvergreenForm.labelLabel')}
                      className="h-14 w-full lg:w-auto lg:min-w-[160px]"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_LABEL_VALUE}>
                        {t('chatEvergreenForm.noLabel')}
                      </SelectItem>
                      {labelOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <InputLabel
                  label={t('chatEvergreenForm.messageLabel')}
                  className="w-full"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  maxLength={CHAT_CONTENT_MAX_LENGTH}
                  disabled={disabled}
                />
                <LengthCounter length={content.length} max={CHAT_CONTENT_MAX_LENGTH} />
              </div>
              {isEditingMessage && (
                <Button
                  type="button"
                  variant="outline"
                  className="h-14 shrink-0"
                  onClick={onEditEnd}
                  disabled={isSubmitting}
                >
                  {t('chatEvergreenForm.cancelEdit')}
                </Button>
              )}
              <Button className="h-14 shrink-0" onClick={handleSubmit} disabled={disabled}>
                {isSubmitting && <Loader />}{' '}
                {isEditingMessage
                  ? t('chatEvergreenForm.saveMessage')
                  : t('chatEvergreenForm.addMessage')}
              </Button>
            </div>
            {!isEditingMessage && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleImportCsvChange}
                  aria-hidden
                />
                <p className="text-sm text-secondary flex items-center gap-1">
                  {t('chatEvergreenForm.orLabel')}
                  <button
                    type="button"
                    onClick={handleExportCsvClick}
                    disabled={!canExport || isExporting || disabled}
                    className="cursor-pointer text-[#2356CC] hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isExporting
                      ? t('chatEvergreenForm.exporting')
                      : t('chatEvergreenForm.exportCsv')}
                  </button>
                  <span>/</span>
                  <button
                    type="button"
                    onClick={handleImportCsvClick}
                    disabled={evergreenChatImportLoading || disabled}
                    className=" cursor-pointer text-[#2356CC] hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {evergreenChatImportLoading
                      ? t('chatEvergreenForm.importing')
                      : t('chatEvergreenForm.importCsv')}
                  </button>
                </p>
              </>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
